import { useState, useCallback } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  Loader2,
  AlertTriangle,
  ArrowRight,
  Download,
  FolderOpen,
} from 'lucide-react';
import { isDesktop } from '@/lib/fileSystem';
import { LANGUAGE_STORAGE_KEY } from '@/lib/i18n';
import LanguagePicker from '@/components/LanguagePicker';

const TOOL_META = {
  qpdf: { label: 'PDF compression', description: 'Compression and password protection' },
  tesseract: { label: 'Tesseract', description: 'Fast OCR engine' },
  python: { label: 'PDF tools', description: 'Forms, images and table conversion' },
  libreoffice: { label: 'LibreOffice', description: 'Document conversion' },
};

const TOOL_ORDER = ['qpdf', 'tesseract', 'python', 'libreoffice'];

function hasStoredLanguage() {
  try {
    return !!window.localStorage?.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return false;
  }
}

export default function Setup({ onComplete }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // On desktop first launch, show the language picker before anything else.
  // On web, Setup isn't shown at all — but guard anyway.
  const [step, setStep] = useState(() =>
    isDesktop() && !hasStoredLanguage() ? 'language' : 'welcome'
  );
  const [installProgress, setInstallProgress] = useState(null);
  const [installResults, setInstallResults] = useState(null);
  const [completedTools, setCompletedTools] = useState([]);
  const [error, setError] = useState(null);

  const startInstall = useCallback(async () => {
    // Online-first model: NO native engines are installed at first run. Heavy
    // ops (compress/OCR/convert) run in the cloud when online; offline users
    // opt into the engine pack later. So first-run setup just completes — no
    // tool downloads, no tool-name installer dialogs, no UAC, no "couldn't
    // install" warnings.
    setError(null);
    setInstallResults(null);
    setStep('ready');
  }, []);

  const handleComplete = useCallback(async () => {
    if (isDesktop()) {
      await window.electronAPI.completeSetup();
    }
    if (onComplete) onComplete();
    navigate('/');
  }, [onComplete, navigate]);

  if (step === 'language') {
    return <LanguagePicker onSelect={() => setStep('welcome')} />;
  }

  if (step === 'welcome') {
    return (
      <div className="min-h-screen flex items-center justify-center p-8"
           style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #fff 40%, #FFF7ED 100%)' }}>
        <div className="max-w-md w-full text-center">
          {/* Scrivar S mark */}
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white font-extrabold text-4xl"
               style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)', boxShadow: '0 8px 32px rgba(79, 70, 229, 0.3)' }}>
            S
          </div>
          <h1 className="text-3xl font-bold text-brand-dark mb-1" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            {t('auth:setup.welcome_title')}<span className="text-accent-coral">.</span>
          </h1>
          <p className="text-sm text-gray-500 mb-1">PDF Suite</p>
          <p className="text-gray-400 text-sm mb-8">
            {t('auth:setup.welcome_subtitle')}
          </p>
          <button
            onClick={startInstall}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-all hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
          >
            {t('common:buttons.get_started')}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  if (step === 'install') {
    const overallPercent = installProgress ? Math.round(installProgress.overall * 100) : 0;

    return (
      <div className="min-h-screen flex items-center justify-center p-8"
           style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #fff 40%, #FFF7ED 100%)' }}>
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                 style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}>
              <Download size={24} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-brand-dark mb-1">Setting up Scrivar</h1>
            <p className="text-sm text-gray-400">
              Downloading tools for the best PDF experience
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {installProgress?.label
                  ? `Installing ${installProgress.label}...`
                  : 'Preparing...'}
              </span>
              <span className="text-sm font-semibold text-brand-blue">{overallPercent}%</span>
            </div>
            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${overallPercent}%`, background: 'linear-gradient(90deg, #4F46E5, #6366F1)' }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-1 shadow-sm">
            {TOOL_ORDER.map((toolName) => {
              const meta = TOOL_META[toolName];
              const isCompleted = completedTools.includes(toolName);
              const isCurrent = installProgress?.currentTool === toolName && !isCompleted;

              return (
                <div key={toolName} className="flex items-center gap-3 py-2.5 px-2 rounded-lg">
                  {isCompleted ? (
                    <CheckCircle size={18} className="text-accent-green shrink-0" />
                  ) : isCurrent ? (
                    <Loader2 size={18} className="text-brand-blue shrink-0 animate-spin" />
                  ) : (
                    <div className="w-[18px] h-[18px] rounded-full border-2 border-gray-200 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isCompleted ? 'text-gray-900' : isCurrent ? 'text-brand-blue' : 'text-gray-400'}`}>
                      {meta.label}
                    </p>
                    <p className="text-xs text-gray-400">{meta.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 text-center mt-4">
            This is a one-time setup. It may take a few minutes depending on your internet connection.
          </p>
          <button
            onClick={handleComplete}
            className="block text-xs text-gray-400 hover:text-gray-600 text-center mt-2 transition-colors mx-auto"
          >
            {t('common:buttons.skip')}
          </button>
        </div>
      </div>
    );
  }

  // Ready step
  const failedTools = installResults
    ? Object.entries(installResults).filter(([, r]) => !r.success)
    : [];

  // Manual-locate escape hatch: when a download fails (typically LibreOffice
  // on a flaky mirror), let the user point us at an existing install they
  // already have. Promotes the failed tool to a "success (skipped)" in-place
  // so the wizard doesn't keep nagging.
  const handleLocateManually = async (toolName) => {
    if (!isDesktop() || !window.electronAPI?.locateToolManually) return;
    const result = await window.electronAPI.locateToolManually(toolName);
    if (result?.ok) {
      setInstallResults((prev) => ({
        ...prev,
        [toolName]: { success: true, skipped: true },
      }));
    } else if (result?.message && result.message !== 'Cancelled') {
      setError(`${TOOL_META[toolName]?.label || toolName}: ${result.message}`);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8"
         style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #fff 40%, #FFF7ED 100%)' }}>
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 bg-accent-green/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={36} className="text-accent-green" />
        </div>
        <h1 className="text-3xl font-bold text-brand-dark mb-2" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
          {t('auth:setup.ready_title')}
        </h1>
        <p className="text-gray-400 mb-6">
          {t('auth:setup.ready_subtitle')}
        </p>

        {failedTools.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-amber-500" />
              <span className="text-sm font-medium text-amber-800">
                {failedTools.length} tool{failedTools.length !== 1 ? 's' : ''} couldn't be installed
              </span>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              If you already have one of these on your computer, point us at it.
              Otherwise these features fall back to browser-based alternatives — you can retry later from Settings.
            </p>
            {failedTools.map(([name, result]) => (
              <div key={name} className="flex items-start justify-between gap-2 py-1.5">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-amber-800">
                    {TOOL_META[name]?.label || name}
                  </p>
                  <p className="text-xs text-amber-600 truncate">{result.error}</p>
                </div>
                {isDesktop() && (
                  <button
                    onClick={() => handleLocateManually(name)}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-800 bg-white border border-amber-300 rounded hover:bg-amber-100 transition-colors"
                  >
                    <FolderOpen size={12} />
                    Locate
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <button
          onClick={handleComplete}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-all hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
        >
          {t('auth:setup.finish')}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
